import * as React from 'react';
import { SubmitGuestCardDto, createGuestCard, addReservation } from '../../../../api/TourSignup';
import PreferencesInputs, { allTourTypeOptions } from '../../../common/PreferencesInputs';
import { useContext, useEffect, useState } from 'react';
import { PropertyContext } from './TourRouter';
import FormSelectLabeled from '../../../common/form/SelectLabeled';
import { Event, Property } from '../../../../api/Property';
import Form from '../../../common/form/Form';
import { PreferredTourFormat } from '../../../../utilities/Enums';
import LoadingOverlay from '../../../common/LoadingOverlay';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import {
    ProspectUserPreference,
    updateProspectUserPreferences
} from '../../../../api/ProspectUserProfile';
import { browserHistory } from '../../../../utilities/history';
import { prospectPages, prospectPath } from '../ProspectRouter';
import TourTimeSlotInput from '../../../common/TourTimeSlotInput';
import '../../../../styles/GuestCardForm.css';
import * as moment from 'moment';
import { momentDateFormat } from '../../../../utilities/formatting';

interface Props {
    initialGuestCard: SubmitGuestCardDto;
    goForward: (guestCardGuid?: string | undefined, queryString?: string | undefined) => void;
}

export const apartmentOptions = (propertyInfo: Property) =>
    propertyInfo.unitTypes.map(unitType => ({
        value: unitType.id,
        label: unitType.description
    }));

const tourTypeOptions = (propertyInfo: Property) => {
    let options = allTourTypeOptions;
    if (!propertyInfo.tour24ToursAvailable) {
        options = options.filter(o => o.value !== PreferredTourFormat.SelfGuided);
    }

    if (!propertyInfo.virtualToursAvailable) {
        options = options.filter(o => o.value !== PreferredTourFormat.Virtual);
    }

    return options;
};

const GuestCardForm = (props: Props) => {
    const property = useContext(PropertyContext);
    if (!property) {
        return null;
    }

    const [selectedTourDate, setSelectedTourDate] = useState<string | undefined>(undefined);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<Event | undefined>(undefined);
    const [wasValidated, setWasValidated] = useState(false);
    const [guestCard, setGuestCard] = useState(props.initialGuestCard);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [moveInDateError, setMoveInDateError] = useState<string | undefined>(undefined);
    const setPreferences = (preferences: ProspectUserPreference) =>
        setGuestCard({ ...guestCard, preferences });

    const tourTypes = tourTypeOptions(property);
    useEffect(() => {
        if (tourTypes.length === 1) {
            setPreferences({ ...guestCard.preferences, preferredTourFormat: tourTypes[0].value, desiredLeaseLength: undefined });
        } else if (guestCard.preferences.preferredTourFormat === undefined) {
            if (tourTypes.find(tt => tt.value === PreferredTourFormat.SelfGuided) !== undefined) {
                setPreferences({
                    ...guestCard.preferences,
                    preferredTourFormat: PreferredTourFormat.SelfGuided,
                    desiredLeaseLength: undefined
                });
            }
        } else {
            setPreferences({
                ...guestCard.preferences,
                desiredLeaseLength: undefined
            })
        }
    }, tourTypes);

    const isSelfGuided =
        guestCard.preferences.preferredTourFormat === PreferredTourFormat.SelfGuided;
    const isVirtual = guestCard.preferences.preferredTourFormat === PreferredTourFormat.Virtual;

    const validateMoveInDate = (preferences: ProspectUserPreference) => {
        const desiredMoveInDate = preferences.desiredMoveInDate;
        if (!desiredMoveInDate) {
            setMoveInDateError(undefined);
            return true;
        }

        const moveIn = moment(desiredMoveInDate, momentDateFormat, true);
        if (!moveIn.isValid()) {
            setMoveInDateError('Please select a valid date');
            return false;
        }

        const today = moment().startOf('day');
        if (moveIn.isBefore(today, 'day')) {
            setMoveInDateError('That move-in date already passed. Pick a future date.');
            return false;
        }

        if (selectedTourDate) {
            const tourDate = moment(selectedTourDate, momentDateFormat, true);
            if (tourDate.isValid() && moveIn.isBefore(tourDate, 'day')) {
                setMoveInDateError('Move in date must be on or after your tour date.');
                return false;
            }
        }

        setMoveInDateError(undefined);
        return true;
    };


    const submitGuestCard = async (guestCardSubmission: SubmitGuestCardDto) => {
        if (!validateMoveInDate(guestCardSubmission.preferences)) {
            setWasValidated(true);
            return;
        }
        if (isSelfGuided && !selectedTimeSlot) {
            setError('Please select a time');
            return;
        }
        setLoading(true);

        const guestCardResult = await createGuestCard(property.id, guestCardSubmission);
        setError(guestCardResult.error);

        if (!guestCardResult.data || guestCardResult.error) {
            setLoading(false);
            return;
        }

        // intentionally not awaited - failure here doesn't imact guest card submission
        updateProspectUserPreferences(guestCardSubmission.preferences);
        if (isSelfGuided) {
            const reservationResult = await addReservation(
                guestCardResult.data.prospectToken,
                selectedTimeSlot!
            );
            if (reservationResult.error) {
                setError(reservationResult.error);
                setLoading(false);
                return;
            }
        }

        setLoading(false);

        if (isVirtual) {
            browserHistory.push(prospectPath(prospectPages.tours));
            return;
        }

        props.goForward(guestCardResult.data.prospectToken);
    };

    return (
        <Form
            onSubmit={() => submitGuestCard(guestCard)}
            submitButtonText="Next"
            wasFormValidated={wasValidated}
            onFormValidated={() => setWasValidated(true)}
            inBootstrapColumn={true}
            buttonGroupClassName="navigation-buttons"
        >
            <LoadingOverlay show={loading} inBootstrapColumn={true} />
            <FormValidationDisplay errorMessage={error} />

            {!isVirtual && (
                <label className="muted-warning">
                    Complete registration within 15 minutes to secure your tour time.
                </label>
            )}

            {tourTypes.length > 1 && (
                <FormSelectLabeled
                    selectProps={{
                        id: 'self-guided',
                        name: 'preferredTourFormat',
                        value: guestCard.preferences.preferredTourFormat,
                        required: true,
                        placeholder: 'Select a tour type...'
                    }}
                    labelProps={{
                        labelText: 'Type of Tour',
                        invalidText: 'Please select a tour type'
                    }}
                    selectOptions={tourTypeOptions(property)}
                    onChange={(_name, value) =>
                        typeof value === 'number' &&
                        setPreferences({
                            ...guestCard.preferences,
                            preferredTourFormat: value
                        })
                    }
                />
            )}

            {tourTypes.length === 1 && isVirtual && (
                <p>
                    <label className="font-weight-bold">
                        NOTE: We are only offering VIRTUAL (and not in person) tours at this time.
                    </label>
                </p>
            )}

            {tourTypes.length === 1 && isSelfGuided && (
                <p>
                    <label className="font-weight-bold">Self-Guided Tour</label>
                </p>
            )}

            {isSelfGuided && (
                <TourTimeSlotInput
                    propertyId={property.id}
                    event={selectedTimeSlot}
                    date={selectedTourDate}
                    onDateChanged={date => {
                        setSelectedTourDate(date);
                        setError(undefined);
                    }}
                    onTimeslotChanged={time => {
                        setSelectedTimeSlot(time);
                        setError(undefined);
                    }}
                />
            )}

            <FormSelectLabeled
                selectProps={{
                    id: 'apartment-type',
                    name: 'apartmentType',
                    value: guestCard.unitTypeId,
                    required: true,
                    placeholder: 'Select Desired Floor Plan'
                }}
                labelProps={{
                    labelText: 'Select desired floor plan',
                    invalidText: 'Please select a unit type'
                }}
                selectOptions={apartmentOptions(property)}
                onChange={(_name, value) =>
                    typeof value === 'number' && setGuestCard({ ...guestCard, unitTypeId: value })
                }
            />

            <PreferencesInputs
                preferences={guestCard.preferences}
                setPreferences={preferences => {
                    setPreferences(preferences);
                    setError(undefined);
                    validateMoveInDate(preferences);
                }}
                gatherPetInformation={property.requestPetInformation}
                petPolicyHelp={property.petPolicy}
                gatherDesiredLeaseLength={property.requestDesiredLeaseLength}
                moveInDateError={moveInDateError}
            />
        </Form>
    );
};

export default GuestCardForm;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/signup/GuestCardForm.tsx